import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { translations } from '../../../../utils/language';

// Preview que simula uma página A4 com header/rodapé e a imagem posicionada com opacidade
// Não gera um PDF real, mas mostra como ficará.
const PdfPreviewWatermark = () => {
  const controlConfig = useSelector((state) => state.reducerControlConfig);
  const language = useSelector((state) => state.reducerLanguage);
  // Parse seguro do watermark_config (pode ser string JSON ou objeto ou undefined)
  const rawConfig = controlConfig?.watermark_config;
  let parsedConfig = {};
  if (typeof rawConfig === 'string' && rawConfig) {
    try {
      parsedConfig = JSON.parse(rawConfig);
    } catch (_) {
      parsedConfig = {};
    }
  } else if (rawConfig && typeof rawConfig === 'object') {
    parsedConfig = rawConfig;
  }

  const headerColor = parsedConfig?.watermark_text_color || '#000000';
  const footerColor = parsedConfig?.watermark_text_color || '#000000';
  const headerText = parsedConfig?.watermark_header_text || "";
  const footerText = parsedConfig?.watermark_footer_text || "";
  const opacity = ((parsedConfig?.watermark_opacity ?? 50) / 100);
  const position = parsedConfig?.watermark_image_position ?? 'MiddleLeft';

  // Medidas dinâmicas da página para posicionamento absoluto
  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const [dims, setDims] = useState({ pageH: 0, headerH: 0, footerH: 0 });

  useEffect(() => {
    const measure = () => {
      const pageH = pageRef.current?.clientHeight || 0;
      const headerH = headerRef.current?.offsetHeight || 0;
      const footerH = footerRef.current?.offsetHeight || 0;
      setDims({ pageH, headerH, footerH });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const getPositionStyle = (pos) => {
    const base = { position: 'absolute', width: 100, height: 100, objectFit: 'contain', opacity, filter: `opacity(${opacity})` };
    const side = 20; // 20px das laterais
    const { pageH, headerH, footerH } = dims;
    const avail = Math.max(pageH - headerH - footerH, 0);
    const centerY = headerH + Math.max((avail / 2) - 50, 0); // -50 para centralizar a imagem 100px
    switch (pos) {
      case 'TopLeft':
        return { ...base, top: headerH + 20, left: side };
      case 'TopCenter':
        return { ...base, top: headerH + 20, left: '50%', transform: 'translateX(-50%)' };
      case 'TopRight':
        return { ...base, top: headerH + 20, right: side };
      case 'MiddleLeft':
        return { ...base, top: centerY, left: side };
      case 'MiddleCenter':
        return { ...base, top: centerY, left: '50%', transform: 'translateX(-50%)' };
      case 'MiddleRight':
        return { ...base, top: centerY, right: side };
      case 'BottomLeft':
        return { ...base, bottom: footerH + 20, left: side };
      case 'BottomCenter':
        return { ...base, bottom: footerH + 20, left: '50%', transform: 'translateX(-50%)' };
      case 'BottomRight':
        return { ...base, bottom: footerH + 20, right: side };
      default:
        return { ...base, top: centerY, left: '50%', transform: 'translateX(-50%)' };
    }
  };

  return (
    <div
      ref={pageRef}
      style={{
        width: '100%',
        maxWidth: 600,
        aspectRatio: '1 / 1.4142', // A4 approx (210x297)
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      {headerText && (
        <div
          ref={headerRef}
          style={{
            background: 'transparent',
            color: headerColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 600,
            padding: '0 16px',
            textAlign: 'center',
          }}
        >
          {headerText}
        </div>
      )}
      {/* Corpo com área para posicionamento */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        {controlConfig?.watermark_image ? (
          <img
            src={controlConfig?.watermark_image}
            alt={translations["marca-agua"][language.data]}
            style={getPositionStyle(position)}
          />
        ) : controlConfig?.watermark_image_url ? (
          <img
            src={controlConfig?.watermark_image_url}
            alt={translations["marca-agua"][language.data]}
            style={getPositionStyle(position)}
          />
        ) : (
          <div
            style={{
              color: '#9ca3af',
              fontSize: 14,
              textAlign: 'center',
              padding: 0,
            }}
          >
            {/* vazio quando não há imagem */}
          </div>
        )}
      </div>

      {/* Footer */}
      {footerText && (
        <div
          ref={footerRef}
          style={{
            background: 'transparent',
            color: footerColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 500,
            padding: '0 16px',
            textAlign: 'center',
          }}
        >
          {footerText}
        </div>
      )}
    </div>
  );
};

export default PdfPreviewWatermark;
